import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '~/components/Avatar';
import Button from '~/components/ui/Button';
import Dialog from '~/components/ui/Dialog';
import { dialogActionsClassName } from '~/components/ui/dialogStyles';
import Icon from '~/components/ui/Icon';
import { SkeletonCircle } from '~/components/ui/Skeleton';
import { cn } from '~/lib/utils';
import useCurrentUser from '~/modules/user/useCurrentUser';
import { useRemoveAvatar } from '~/modules/user/useRemoveAvatar';
import { useUpdateAvatar } from '~/modules/user/useUpdateAvatar';

export default function AvatarSection({ overview = false }: { overview?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftAvatar, setDraftAvatar] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { mutateAsync: updateAvatar, isPending: isUpdating } = useUpdateAvatar();
  const { mutateAsync: removeAvatarMutation, isPending: isRemoving } = useRemoveAvatar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPending = isUpdating || isRemoving;
  const originalAvatar = user?.avatar || null;
  const isOpenRef = useRef(false);

  const revokeDraftAvatar = (url: string | null) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const openDialog = () => {
    setDraftAvatar(originalAvatar);
    setIsDirty(false);
    setFileError(null);
    isOpenRef.current = true;
    setIsOpen(true);
  };

  const closeDialog = () => {
    setDraftAvatar((currentAvatar) => {
      revokeDraftAvatar(currentAvatar);
      return originalAvatar;
    });
    setIsDirty(false);
    setFileError(null);
    isOpenRef.current = false;
    setIsOpen(false);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFileError(t('profile.avatarInvalidType'));
    } else if (file.size > 5 * 1024 * 1024) {
      setFileError(t('profile.avatarTooLarge'));
    } else {
      const blobUrl = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        if (!isOpenRef.current) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setDraftAvatar((currentAvatar) => {
          revokeDraftAvatar(currentAvatar);
          return blobUrl;
        });
        setIsDirty(true);
        setFileError(null);
      };
      probe.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        setFileError(t('profile.avatarLoadError'));
      };
      probe.src = blobUrl;
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAvatar = () => {
    setDraftAvatar((currentAvatar) => {
      revokeDraftAvatar(currentAvatar);
      return null;
    });
    setIsDirty(Boolean(originalAvatar));
    setFileError(null);
  };

  const saveAvatar = async () => {
    if (!isDirty) {
      closeDialog();
      return;
    }

    if (draftAvatar?.startsWith('blob:')) {
      await updateAvatar(draftAvatar);
    } else if (!draftAvatar && originalAvatar) {
      await removeAvatarMutation();
    }

    closeDialog();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        aria-label={t('profile.uploadAvatar')}
        variant="ghost"
        size="min"
        className="group relative shrink-0 rounded-full outline-none"
        onPress={openDialog}
      >
        {isPending ? (
          <SkeletonCircle size={88} />
        ) : (
          <Avatar
            url={user?.avatar}
            size={88}
            className={cn(
              'ring-4 ring-avatar-ring',
              overview
                ? 'compact:size-14 compact:text-[56px] max-xs:size-12 max-xs:text-5xl'
                : 'compact:size-11 compact:text-[44px]',
            )}
          />
        )}
        <span className="absolute -right-0.5 bottom-0 flex size-7 items-center justify-center rounded-full border-3 border-avatar-camera-border bg-avatar-camera-background text-avatar-camera transition-colors group-hover:bg-avatar-camera-hover compact:size-6 compact:border-2">
          <Icon name="photo_camera" className="text-[14px]" />
        </span>
      </Button>

      <Dialog show={isOpen} onClose={closeDialog} aria-label={t('profile.avatarTitle')}>
        <h2 className="mt-1 pr-8 text-[27px] leading-[1.2] tracking-[-0.6px] font-semibold">
          {t('profile.avatarTitle')}
        </h2>
        <p className="mt-3 text-[14px] leading-[1.8] text-body">{t('profile.avatarDescription')}</p>

        <div className="my-7 flex justify-center compact:my-4">
          <Avatar url={draftAvatar} size={136} className="compact:size-24 compact:text-8xl" />
        </div>

        <div className="flex flex-col items-center gap-5 compact:gap-3 compact:[&>button]:min-h-11">
          <Button
            type="button"
            variant="secondary"
            size="md"
            className="gap-2 border border-card-border px-5"
            onPress={handleFileSelect}
            isDisabled={isPending}
          >
            <Icon name="upload" className="text-[18px]" />
            {t('profile.chooseAvatar')}
          </Button>
          {draftAvatar && (
            <button
              type="button"
              onClick={removeAvatar}
              disabled={isPending}
              className="text-[14px] text-destruction hover:brightness-110 disabled:opacity-40"
            >
              {t('profile.removeAvatar')}
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-[12px] text-gray-3">{t('profile.avatarHint')}</p>
        <p
          role="alert"
          className="mt-2 min-h-5 text-center text-[13px] leading-[1.7] text-light-error compact:empty:hidden"
        >
          {fileError}
        </p>

        <div className={cn('mt-6 flex justify-end gap-2.5', dialogActionsClassName)}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-11 border border-card-border"
            onPress={closeDialog}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-11"
            onPress={saveAvatar}
            isPending={isPending}
            isDisabled={isPending}
          >
            {t('profile.saveChanges')}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
